import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PaymentAgentDto } from 'api/Wallet/Types/PaymentAgentDto'
import AddButton from 'components/AddButton/AddButton'
import { FlexBox } from 'components/Box'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { hasPermission } from 'utils/permissionsValidator'

import { ClientNavigation, ClientTab } from '../Navigation/ClientNavigation'
import AddClientPOAForm from './AddClientPOA'
import clientDetailsPOAColumns from './clientDetailsPOAcolumns'

interface Props {
  data: ClientDetailedDto
  forceRefresh(): void
  updateClientStatus(values: UpdateClientStatus): void
}
const ClientDetailsPOA: React.FC<Props> = ({ data, forceRefresh, updateClientStatus }) => {
  const [auth] = useContext(AuthSessionContext)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [isLoading, setLoading] = useState<boolean>(false)
  const [acceptPOAModal, setAcceptPOAModal] = useState<boolean>(false)
  const [paymentAgents, setPaymentAgents] = useState<null | PaymentAgentDto[]>(null)
  const permissions = auth?.permissions || []

  const fetchPA = useCallback(async () => {
    try {
      setLoading(true)
      const url = `Client == ${data.id}`
      const result = await walletsApiClient.getPaymentAgentsClientQuery(url)
      const paymentAgentDtos: PaymentAgentDto[] = result.items
      setPaymentAgents(paymentAgentDtos)
      setLoading(false)
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchData = async () => {
      await fetchPA()
    }
    fetchData()
  }, [fetchPA])

  const onCloseModal = () => {
    setAcceptPOAModal(false)
    fetchPA()
  }

  const renderAddWalletButton = (): React.ReactNode => (
    <AddButton
      disabled={!hasPermission(permissions, PermissionsEnum.CanAcceptClientPowerOfAttorney)}
      text={'Manually accept POA'}
      onClick={() => setAcceptPOAModal(true)}
    />
  )

  const handleRemove = async (paymentAgentId: string) => {
    await walletsApiClient.removePaymentAgent(paymentAgentId, data.id)
    forceRefresh()
  }

  if (isLoading) {
    return <Spinner />
  }
  return (
    <Fragment>
      <ClientNavigation
        isEdit={false}
        data={data}
        tab={ClientTab.POA}
        updateClientStatus={updateClientStatus}
      />
      <FlexBox
        alignItems={'center'}
        flexDirection='column'
        justifyContent={'flex-start'}
        style={{ margin: '0 2.5rem' }}
      >
        {acceptPOAModal && (
          <Modal visible={acceptPOAModal} onClose={onCloseModal}>
            <AddClientPOAForm client={data} onClose={onCloseModal} />
          </Modal>
        )}
        {renderAddWalletButton()}
        <Grid data={paymentAgents} resizable>
          {clientDetailsPOAColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <Column key={idx + 2} {...column} />
            ))}
          <Column
            width={50}
            title=''
            filterCell={() => null}
            cell={(props: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Remove',
                      'data-test': 'remove',
                      onClick: () => handleRemove(props.dataItem.paymentAgent.id),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        </Grid>
      </FlexBox>
    </Fragment>
  )
}
export default ClientDetailsPOA
